import type { Outcome } from "@landing/types"
import NumberFlow from "@number-flow/react"
import { memo } from "react"
import { isOutcomeEliminated } from "../utils/outcome-utils"

interface OutcomePriceDisplayProps {
  outcome: Outcome
  displayMode: "chance" | "price"
  state: "live" | "upcoming" | "final"
  isFinal: boolean
  heldOutcomes: string[]
}

export const OutcomePriceDisplay = memo(function OutcomePriceDisplay({
  outcome,
  displayMode,
  state,
  isFinal,
  heldOutcomes,
}: OutcomePriceDisplayProps) {
  const isEliminated = isOutcomeEliminated(outcome, heldOutcomes)
  const price = Number.parseFloat(outcome.price)

  const textClass = `text-right ${isFinal ? "mr-3.5" : "mr-1"} ${isEliminated ? "text-white/40" : ""}`

  if (displayMode === "chance") {
    return (
      <span className={textClass}>
        {state === "upcoming" ? (
          <span className={!isEliminated ? "opacity-50" : ""}>0%</span>
        ) : isFinal ? (
          isEliminated || price !== 100 ? (
            <span className={!isEliminated ? "opacity-50" : ""}>0%</span>
          ) : (
            <>
              <NumberFlow
                value={100}
                format={{
                  style: "decimal",
                  useGrouping: false,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }}
                transformTiming={{ duration: 300, easing: "ease" }}
                trend={(oldValue, newValue) => (oldValue < newValue ? 1 : -1)}
              />
              %
            </>
          )
        ) : isEliminated ? (
          <span className="opacity-50">0%</span>
        ) : price < 1 ? (
          <>
            <span className="mr-[.1rem] opacity-60">{"<"}</span>1%
          </>
        ) : (
          <>
            <NumberFlow
              value={Math.round(price)}
              format={{
                style: "decimal",
                useGrouping: false,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }}
              transformTiming={{ duration: 300, easing: "ease" }}
              trend={(oldValue, newValue) => (oldValue < newValue ? 1 : -1)}
            />
            %
          </>
        )}
      </span>
    )
  }

  return (
    <span className={textClass}>
      {state === "upcoming" ? (
        <span className={!isEliminated ? "opacity-50" : ""}>0</span>
      ) : isFinal ? (
        isEliminated || price !== 100 ? (
          <span className={!isEliminated ? "opacity-50" : ""}>0</span>
        ) : (
          <NumberFlow
            value={100}
            format={{
              style: "decimal",
              useGrouping: false,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }}
            transformTiming={{ duration: 250, easing: "ease" }}
            trend={(oldValue, newValue) => (oldValue < newValue ? 1 : -1)}
          />
        )
      ) : isEliminated ? (
        <span className="opacity-50">0</span>
      ) : price === 0 ? (
        <span className={!isEliminated ? "opacity-50" : ""}>0</span>
      ) : price < 1 ? (
        price.toFixed(2).substring(1)
      ) : (
        price.toFixed(2)
      )}
    </span>
  )
})
